import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { Renderer } from "../map_creator_v2/renderer"
import { state } from "../map_creator_v2/map_state/state"
import { UnitConverter } from "../map_creator_v2/unit_converter"

export default class extends Controller {
  static targets = ["fileField", "fileName", "uploadLabel", "loader", "loaderElement", "loaderText", "sensorsContainer", "groupsContainer", "mapsContainer"]

  static values = {
    selectedSensors: Array,
    unselectedSensors: Array
  }

  connect() {
    this.project = null
    this.resizeTimer = null
    this.loaderResetTimer = null
    this.pendingUploadsPromise = null
    this.uploadsReady = false
    this.uploadRequestId = 0
    this.resubmitting = false
    this.handleWindowResize = this.handleWindowResize.bind(this)
    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    window.addEventListener("resize", this.handleWindowResize)
    this.form = this.element.closest("form")
    this.form?.addEventListener("submit", this.handleFormSubmit)

    this.refreshFeatureHeightsWhenImagesLoad()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleWindowResize)
    this.form?.removeEventListener("submit", this.handleFormSubmit)
    clearTimeout(this.resizeTimer)
    clearTimeout(this.loaderResetTimer)
  }

  async handleFileChange(event) {
    const file = event.target.files?.[0]
    if (!file) return

    if (this.hasFileNameTarget) this.fileNameTarget.textContent = file.name

    try {
      const project = await this.readProjectFile(file)
      this.project = project
      if(!project.calibration || (project.lenUnit != "m" && project.lenUnit != "ft")) {
        this.fileNameTarget.textContent = "Invalid JSON file - missing or invalid calibration data"
        return
      }
      this.addFields(project)
    } catch (error) {
      console.error(error)
      if (this.hasFileNameTarget) this.fileNameTarget.textContent = "Invalid JSON file"
      this.clearAll()
    }
  }

  async handleFormSubmit(event) {
    if (this.resubmitting) return
    if (!this.pendingUploadsPromise || this.uploadsReady) return

    event.preventDefault()

    try {
      await this.pendingUploadsPromise
      if (!this.uploadsReady) return
      this.resubmitting = true
      this.form?.requestSubmit?.(event.submitter)
      queueMicrotask(() => { this.resubmitting = false })
    } catch (error) {
      console.error(error)
    }
  }

  handleWindowResize() {
    if (!this.project) return

    clearTimeout(this.resizeTimer)
    this.resizeTimer = setTimeout(() => {
      this.refreshFeatureHeights()
    }, 80)
  }

  clearAll() {
    this.project = null
    this.pendingUploadsPromise = null
    this.uploadsReady = false
    this.uploadRequestId += 1
    this.sensorsContainerTarget.innerHTML = ""
    this.groupsContainerTarget.innerHTML = ""
    this.mapsContainerTarget.innerHTML = ""
    this.resetLoader()
    this.refreshFeatureHeights()
  }

  async readProjectFile(file) {
    const text = await file.text()
    const project = JSON.parse(text)

    if (!project?.map?.dataUrl) {
      throw new Error("Missing project.map.dataUrl")
    }

    const mapImage = await this.loadImage(project.map.dataUrl)
    const origImage = await this.loadImage(project.origMap?.dataUrl || project.map.dataUrl)

    return this.normalizeProject(project, mapImage, origImage)
  }

  async loadImage(dataUrl) {
    return await new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = reject
      image.src = dataUrl
    })
  }

  normalizeProject(project, mapImage, origImage) {
    const saved = project.state || {}

    const lenPath = Number(saved.lenPath)
    const calibration = Number.isFinite(lenPath) && lenPath > 0 && !!saved.lenUnit

    const sensors = Array.isArray(saved.sensors) ? saved.sensors : []
    const groups = Array.isArray(saved.groups) ? saved.groups : []
    const segments = Array.isArray(saved.segments) ? saved.segments : []

    const renderSensors = sensors.map((sensor, index) =>
      this.buildRenderSensor(sensor, {calibration, lenPath, lenUnit: saved.lenUnit, fallbackName: sensor?.name || `Sensor ${index + 1}`})
    )

    const sensorNameById = new Map(
      renderSensors.map((sensor, index) => [Number(sensor.id), sensor.name || `Sensor ${index + 1}`])
    )

    return {
      raw: project,
      mapImage,
      origImage,
      mapWorld: {
        x: Number.isFinite(Number(saved.mapWorld?.x)) ? Number(saved.mapWorld.x) : 0,
        y: Number.isFinite(Number(saved.mapWorld?.y)) ? Number(saved.mapWorld.y) : 0
      },
      sensors: renderSensors,
      groups: groups.map((group, index) => ({
        id: Number(group.id),
        name: group.name || `Group ${index + 1}`,
        sensorIds: Array.isArray(group.sensorIds) ? group.sensorIds.map(Number) : []
      })),
      segments: segments.map((segment) => ({
        a: segment?.a ? { ...segment.a } : null,
        b: segment?.b ? { ...segment.b } : null
      })),
      sensorNameById,
      calibration,
      lenPath: calibration ? lenPath : null,
      lenUnit: calibration ? saved.lenUnit : null,
      visibleSensors: saved.visibleSensors ?? true,
      visiblePointDistances: saved.visiblePointDistances ?? true,
      visibleLinearFlows: saved.visibleLinearFlows ?? true,
      visibleLables: saved.visibleLables ?? true
    }
  }

  buildRenderSensor(raw, meta) {
    const zoneRadiusUnit = typeof raw.zoneRadiusUnit === "string" && raw.zoneRadiusUnit.length > 0 ? raw.zoneRadiusUnit : (meta.calibration ? meta.lenUnit : "px")

    const numberOr = (value, fallback) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : fallback
    }

    return {
      id: raw.id,
      name: raw.name || meta.fallbackName,
      position: {
        x: numberOr(raw?.position?.x, 0),
        y: numberOr(raw?.position?.y, 0)
      },
      flowEndSensorIds: Array.isArray(raw.flowEndSensorIds) ? raw.flowEndSensorIds.map(String) : [],
      edgeFadePx: numberOr(raw.edgeFadePx, 18),
      zoneAColor: raw.zoneAColor || "#bf40bf",
      zoneBColor: raw.zoneBColor || "#25db4b",
      sensorColor: raw.sensorColor || "rgba(0,120,255,1)",
      enableZoneA: raw.enableZoneA ?? true,
      enableZoneB: raw.enableZoneB ?? true,
      visible: raw.visible ?? true,
      engagedDuration: numberOr(raw.engagedDuration, 60),
      rssiA: Number.isFinite(Number(raw.rssiA)) ? Math.round(Number(raw.rssiA)) : "",
      rssiB: Number.isFinite(Number(raw.rssiB)) ? Math.round(Number(raw.rssiB)) : "",
      zoneARadius: numberOr(raw.zoneARadius, meta.calibration ? 10 : 80),
      zoneBRadius: numberOr(raw.zoneBRadius, meta.calibration ? 20 : 160),
      zoneRadiusUnit,

      getZonePx() {
        if (meta.calibration && zoneRadiusUnit !== "px") {
          const scale = Number(meta.lenPath)
          if (!Number.isFinite(scale) || scale <= 0) return { a: null, b: null }

          return {
            a: this.enableZoneA ? Number(this.zoneARadius || 0) / scale : null,
            b: this.enableZoneB ? Number(this.zoneBRadius || 0) / scale : null
          }
        }

        return {
          a: this.enableZoneA ? Number(this.zoneARadius || 0) : null,
          b: this.enableZoneB ? Number(this.zoneBRadius || 0) : null
        }
      }
    }
  }

  addFields(project) {
    this.addSensors(project)
    this.addGroups(project)
    this.addMaps(project)
    this.refreshFeatureHeights()
  }

  sensorAssignmentChanged() {
    const sensors = this.collectSensorFormState()
    const groups = this.collectGroupFormState()
    if (!sensors.length) return

    const project = {...(this.project || {}), sensors, groups, sensorNameById: this.sensorNameByIdFrom(sensors) }

    this.addSensors(project, sensors)
    this.addGroups(project)
    this.refreshFeatureHeights()
  }

  collectSensorFormState() {
    const blocks = this.sensorsContainerTarget.querySelectorAll(".event-sensor-block")

    return Array.from(blocks).map((block, index) => {
      const original = this.project?.sensors?.[index] || {}

      const readValue = (selector, fallback = "") =>
        block.querySelector(selector)?.value ?? fallback

      return {
        ...original,
        id: block.dataset.sensorUiId || original.id || index,
        x: readValue('input[name$="[x]"]', original.x ?? 0),
        y: readValue('input[name$="[y]"]', original.y ?? 0),
        name: readValue('[data-sensor-field="name"]', original.name || ""),
        selectedExSensorId: readValue('[data-sensor-field="select"]', ""),
        engagedDuration: readValue('[data-sensor-field="engagedDuration"]', original.engagedDuration ?? 60),
        rssiA: readValue('[data-sensor-field="rssiA"]', original.rssiA ?? ""),
        rssiB: readValue('[data-sensor-field="rssiB"]', original.rssiB ?? "")
      }
    })
  }

  collectGroupFormState() {
    const groupItems = this.groupsContainerTarget.querySelectorAll(".step-form-row-item, .step-form-row-grid-item")

    return Array.from(groupItems).map((group, index) => {
      const name = group.querySelector('input[name$="[name]"]')?.value || `Group ${index + 1}`
      const sensorIds = Array.from(group.querySelectorAll("[data-group-sensor-ui-id]"))
        .map((cell) => Number(cell.dataset.groupSensorUiId))
        .filter((value) => Number.isFinite(value))

      return { id: index, name, sensorIds }
    }).filter((group) => group.sensorIds.length)
  }

  sensorNameByIdFrom(sensors) {
    return new Map(sensors.map((sensor, index) => [Number(sensor.id), sensor.name || `Sensor ${index + 1}`]))
  }

  saveDestroyFields(element) {
    let form = element.closest("form")
    element.querySelectorAll("[name*='[id]'], [name*='[_destroy]']").forEach(input => {
      input.removeAttribute("form")
      form.appendChild(input)
    })
  }

  addSensors(project, sensors = null) {
    this.saveDestroyFields(this.sensorsContainerTarget)

    const sensorState = sensors || project.sensors.map((sensor) => ({...sensor, selectedExSensorId: "" }))

    if (!sensorState.length) {
      this.sensorsContainerTarget.innerHTML = ""
      return
    }

    const assignedValues = sensorState
      .map((sensor) => String(sensor.selectedExSensorId || ""))
      .filter(Boolean)

    this.sensorsContainerTarget.innerHTML = sensorState.map((sensor, index) => {
      const html = this.sensorTemplate(sensor, index, assignedValues)
      const separator = index < sensorState.length - 1 ? `<hr>` : ""
      return `${html}${separator}`
    }).join("")
  }

  sensorKey(sensor, index) {
    const rawId = sensor?.id
    if (rawId !== null && rawId !== undefined && String(rawId).trim() !== "") {
      return String(rawId)
    }

    return String(index)
  }

  sensorFieldName(sensor, index, field) {
    return `event[ex_allocations_attributes][${this.sensorKey(sensor, index)}][${field}]`
  }

  sensorMapPosition(sensor) {
    const x = Number(sensor?.x)
    const y = Number(sensor?.y)

    if (Number.isFinite(x) && Number.isFinite(y)) {
      return { x, y }
    }

    return {
      x: Number(sensor.position?.x || 0) - Number(this.project?.mapWorld?.x || 0),
      y: Number(sensor.position?.y || 0) - Number(this.project?.mapWorld?.y || 0)
    }
  }

  allSensorChoices() {
    const normalize = (rows) =>
      (rows || []).map(([physicalId, id]) => ({ id: String(id), physicalId: String(physicalId) }))

    return [...normalize(this.selectedSensorsValue), ...normalize(this.unselectedSensorsValue)]
      .filter((item, index, items) => items.findIndex((other) => other.id === item.id) === index)
      .sort((a, b) => a.physicalId.localeCompare(b.physicalId, undefined, { numeric: true }))
  }

  sensorTemplate(sensor, index, assignedValues) {
    const uid = `imported-sensor-${index}`
    const options = this.sensorOptionsMarkup(sensor.selectedExSensorId, assignedValues)
    const mapPosition = this.sensorMapPosition(sensor)

    return `
      <div class="event-sensor-block" data-sensor-index="${index}" data-sensor-ui-id="${this.escapeHtml(sensor.id)}">
        <input type="hidden" name="${this.sensorFieldName(sensor, index, "map_id")}" value="${this.escapeHtml(sensor.id)}">
        <input type="hidden" name="${this.sensorFieldName(sensor, index, "x")}" value="${this.escapeHtml(mapPosition.x)}">
        <input type="hidden" name="${this.sensorFieldName(sensor, index, "y")}" value="${this.escapeHtml(mapPosition.y)}">
        <div class="step-form-row">
          <div class="step-form-row-item">
            <label for="${uid}-name" class="step-form-label required">Sensor Name</label>
            <input type="text" name="${this.sensorFieldName(sensor, index, "name")}" class="step-form-input" id="${uid}-name" value="${this.escapeHtml(sensor.name || "")}" required data-sensor-field="name" data-action="input->event-sensors-step-form#sensorNameChanged">
          </div>
          <div class="step-form-row-item required">
            <label for="${uid}-allocation" class="step-form-label required">Sensor ID</label>
            <select name="${this.sensorFieldName(sensor, index, "ex_sensor_id")}" class="step-form-input" id="${uid}-allocation" data-controller="form-select" data-action="change->event-sensors-step-form#sensorAssignmentChanged" data-sensor-field="select">
              ${options}
            </select>
          </div>
        </div>
        <div class="step-form-row">
          <div class="step-form-row-item">
            <label for="${uid}-engaged-duration" class="step-form-label required">Engaged Duration (Seconds)</label>
            <input type="number" name="${this.sensorFieldName(sensor, index, "min_engaged_duration")}" class="step-form-input" id="${uid}-engaged-duration" required value="${this.escapeHtml(sensor.engagedDuration)}" data-sensor-field="engagedDuration">
          </div>
          <div class="step-form-row-item">
            <label for="${uid}-min-mean" class="step-form-label required">Max Engaged Signal Strength</label>
            <input type="number" name="${this.sensorFieldName(sensor, index, "min_mean")}" class="step-form-input" id="${uid}-min-mean" required value="${this.escapeHtml(sensor.rssiA)}" data-sensor-field="rssiA">
          </div>
          <div class="step-form-row-item">
            <label for="${uid}-max-mean" class="step-form-label required">Max Passed Signal Strength</label>
            <input type="number" name="${this.sensorFieldName(sensor, index, "max_mean")}" class="step-form-input" id="${uid}-max-mean" required value="${this.escapeHtml(sensor.rssiB)}" data-sensor-field="rssiB">
          </div>
        </div>
      </div>
    `
  }

  sensorOptionsMarkup(currentValue, assignedValues) {
    const currentValueString = String(currentValue || "")
    const allSensors = this.allSensorChoices()

    const takenByOthers = new Set( assignedValues.map(String).filter((value) => value && value !== currentValueString))

    let available = allSensors.filter((item) => !takenByOthers.has(item.id))

    if (currentValueString && !available.some((item) => item.id === currentValueString)) {
      const fallback =
        allSensors.find((item) => item.id === currentValueString) ||
        { id: currentValueString, physicalId: currentValueString }

      available = [fallback, ...available]
    }

    const blankSelected = currentValueString === ""

    return `
      <option value="" ${blankSelected ? "selected" : ""}>Assign Sensor</option>
      ${available.map((item) => `
        <option value="${this.escapeHtml(item.id)}" ${item.id === currentValueString ? "selected" : ""}>
          ${this.escapeHtml(item.physicalId)}
        </option>
      `).join("")}
    `
  }

  addGroups(project) {
    this.saveDestroyFields(this.groupsContainerTarget)

    if (!project.groups.length) {
      this.groupsContainerTarget.innerHTML = ""
      return
    }

    const rows = this.chunk(project.groups, 3).map((groupChunk, rowIndex) => {
      return `
        <div class="step-form-row">
          ${groupChunk.map((group, colIndex) => this.groupTemplate(group, rowIndex * 3 + colIndex, project)).join("")}
        </div>
      `
    })

    this.groupsContainerTarget.innerHTML = rows.join("")
  }

  groupTemplate(group, index, project) {
    const uid = `imported-group-${index}`

    const sensorInputs = group.sensorIds.map((sensorId) => {
      return `<input type="hidden" name="${this.groupFieldName(index, "sensor_map_ids")}[]" value="${this.escapeHtml(sensorId)}">`
    }).join("")

    const sensorCells = group.sensorIds.map((sensorId) => {
      const sensorName = project.sensorNameById.get(Number(sensorId)) || `Sensor ${sensorId}`

      return `<div class="step-form-grid-cell" data-group-sensor-ui-id="${this.escapeHtml(sensorId)}"> ${this.escapeHtml(sensorName)}</div>`
    }).join("")

    return `
      <div class="step-form-row-item">
        <label for="${uid}-name" class="step-form-label required">Group Name</label>
        <input type="text" name="${this.groupFieldName(index, "name")}" value="${this.escapeHtml(group.name)}" class="step-form-input" id="${uid}-name" required>
          ${sensorInputs}
        <div class="step-form-grid">${sensorCells}</div>
      </div>
    `
  }

  addMaps(project) {
    const originalImageDataUrl = project.origImage.src
    const updatedImageDataUrl = this.renderUpdatedImageDataUrl(project)
    const workingWidth = project.mapImage.naturalWidth
    const workingHeight = project.mapImage.naturalHeight
    const physicalScale = this.physicalScaleInMeters(project)

    this.mapsContainerTarget.innerHTML = `
      <div class="step-form-row">
        <div class="step-form-row-item">
          <label class="step-form-label">Original Map</label>
          <div class="step-form-input">
            <img src="${this.escapeHtml(originalImageDataUrl)}" alt="Original Map" style="display:block;width:100%;height:auto;">
            <input type="hidden" data-map-signed-id="original" name="event[original_map_attributes][image_signed_id]" value="">
          </div>
        </div>
        <div class="step-form-row-item">
          <label class="step-form-label">Updated Map</label>
          <div class="step-form-input">
            <img src="${this.escapeHtml(updatedImageDataUrl)}" alt="Updated Map" style="display:block;width:100%;height:auto;">
            <input type="hidden" data-map-signed-id="updated" name="event[map_attributes][image_signed_id]" value="">
            <input type="hidden" name="event[map_attributes][working_width]" value="${workingWidth}">
            <input type="hidden" name="event[map_attributes][working_height]" value="${workingHeight}">
            <input type="hidden" name="event[map_attributes][physical_scale]" value="${physicalScale}">
          </div>
        </div>
      </div>
    `

    this.startMapUploads(originalImageDataUrl, updatedImageDataUrl)
    this.refreshFeatureHeightsWhenImagesLoad()
  }

  async startMapUploads(originalImageDataUrl, updatedImageDataUrl) {
    const requestId = ++this.uploadRequestId
    this.uploadsReady = false
    this.showLoader("Uploading Maps")

    const eventName = (document.querySelector("[name='event[name]']")?.value || "event").trim().replace(/[^\w\-]+/g, "_")

    const originalSignedIdInput = this.mapsContainerTarget.querySelector('[data-map-signed-id="original"]')
    const updatedSignedIdInput = this.mapsContainerTarget.querySelector('[data-map-signed-id="updated"]')

    if (originalSignedIdInput) originalSignedIdInput.value = ""
    if (updatedSignedIdInput) updatedSignedIdInput.value = ""

    this.pendingUploadsPromise = Promise.all([
      this.directUploadDataUrl(originalImageDataUrl, `${eventName}_original_map_${Date.now()}.png`),
      this.directUploadDataUrl(updatedImageDataUrl, `${eventName}_updated_map_${Date.now()}.png`)
    ]).then(([originalSignedId, updatedSignedId]) => {
      if (requestId !== this.uploadRequestId) return
      if (originalSignedIdInput) originalSignedIdInput.value = originalSignedId
      if (updatedSignedIdInput) updatedSignedIdInput.value = updatedSignedId
      this.uploadsReady = true
      this.showLoaderSuccess("Maps uploaded successfully")
    }).catch((error) => {
      if (requestId !== this.uploadRequestId) return
      this.uploadsReady = false
      console.error(error)
      this.showLoaderFailure("Map upload failed")
      throw error
    })

    return this.pendingUploadsPromise
  }

  directUploadDataUrl(dataUrl, filename) {
    const file = this.dataUrlToFile(dataUrl, filename)
    return this.directUploadFile(file)
  }

  directUploadFile(file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads")

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }

  dataUrlToFile(dataUrl, filename) {
    const [meta, base64] = dataUrl.split(",")
    const mime = meta.match(/data:(.*?);base64/)?.[1] || "image/png"
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }

    return new File([bytes], filename, { type: mime })
  }

  groupFieldName(index, field) {
    return `event[groups][${index}][${field}]`
  }

  renderUpdatedImageDataUrl(project) {
    const canvas = document.createElement("canvas")
    canvas.width = project.mapImage.naturalWidth
    canvas.height = project.mapImage.naturalHeight

    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(project.mapImage, 0, 0, canvas.width, canvas.height)

    const bounds = {
      left: 0,
      top: 0,
      right: canvas.width,
      bottom: canvas.height
    }

    this.withPreviewState(project, () => {
      const target = Renderer.getRenderTarget({
        ctx,
        zoom: 1,
        labelsOccupied: [],
        worldToScreen: (x, y) => ({
          x: x - project.mapWorld.x,
          y: y - project.mapWorld.y
        }),
        showZoneLabels: true
      })

      Renderer.drawMeasurePath(target)
      if (project.visiblePointDistances) Renderer.drawPointDistances(target)
      if (project.visibleLinearFlows) Renderer.drawLinearFlows(target)
      if (project.visibleSensors) Renderer.drawSensors(target, bounds)
    })

    return canvas.toDataURL("image/png")
  }

  withPreviewState(project, callback) {
    const snapshot = {
      mapImage: state.mapImage,
      origMap: state.origMap,
      mapWorld: state.mapWorld ? { ...state.mapWorld } : { x: 0, y: 0 },
      zoomValue: state.zoomValue,
      sensors: state.sensors,
      segments: state.segments,
      tracerPathWorld: state.tracerPathWorld,
      visibleSensors: state.visibleSensors,
      visiblePointDistances: state.visiblePointDistances,
      visibleLinearFlows: state.visibleLinearFlows,
      visibleLables: state.visibleLables,
      calibration: state.calibration,
      lenPath: state.lenPath,
      lenUnit: state.lenUnit,
      labelsOccupied: state.labelsOccupied,
      selectedSensorId: state.selectedSensorId,
      addSensorMode: state.addSensorMode,
      previewSensor: state.previewSensor,
      linearFlowMode: state.linearFlowMode,
      flowStartSensorId: state.flowStartSensorId,
      flowPreviewWorld: state.flowPreviewWorld,
      pointDistanceMode: state.pointDistanceMode,
      startPoint: state.startPoint,
      cursorWorld: state.cursorWorld
    }

    try {
      state.mapImage = project.mapImage
      state.origMap = project.origImage
      state.mapWorld = { ...project.mapWorld }
      state.zoomValue = 1
      state.sensors = project.sensors
      state.segments = project.segments
      state.tracerPathWorld = []
      state.visibleSensors = project.visibleSensors
      state.visiblePointDistances = project.visiblePointDistances
      state.visibleLinearFlows = project.visibleLinearFlows
      state.visibleLables = project.visibleLables
      state.calibration = project.calibration
      state.lenPath = project.lenPath
      state.lenUnit = project.lenUnit
      state.labelsOccupied = []
      state.selectedSensorId = null
      state.addSensorMode = false
      state.previewSensor = null
      state.linearFlowMode = false
      state.flowStartSensorId = null
      state.flowPreviewWorld = null
      state.pointDistanceMode = false
      state.startPoint = null
      state.cursorWorld = null

      callback()
    } finally {
      state.mapImage = snapshot.mapImage
      state.origMap = snapshot.origMap
      state.mapWorld = snapshot.mapWorld
      state.zoomValue = snapshot.zoomValue
      state.sensors = snapshot.sensors
      state.segments = snapshot.segments
      state.tracerPathWorld = snapshot.tracerPathWorld
      state.visibleSensors = snapshot.visibleSensors
      state.visiblePointDistances = snapshot.visiblePointDistances
      state.visibleLinearFlows = snapshot.visibleLinearFlows
      state.visibleLables = snapshot.visibleLables
      state.calibration = snapshot.calibration
      state.lenPath = snapshot.lenPath
      state.lenUnit = snapshot.lenUnit
      state.labelsOccupied = snapshot.labelsOccupied
      state.selectedSensorId = snapshot.selectedSensorId
      state.addSensorMode = snapshot.addSensorMode
      state.previewSensor = snapshot.previewSensor
      state.linearFlowMode = snapshot.linearFlowMode
      state.flowStartSensorId = snapshot.flowStartSensorId
      state.flowPreviewWorld = snapshot.flowPreviewWorld
      state.pointDistanceMode = snapshot.pointDistanceMode
      state.startPoint = snapshot.startPoint
      state.cursorWorld = snapshot.cursorWorld
    }
  }

  showLoader(message = "Uploading Maps") {
    clearTimeout(this.loaderResetTimer)
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.add("hide")
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hide", "success", "failure")
      this.loaderTarget.classList.add("show")
    }
    if (this.hasLoaderTextTarget) this.loaderTextTarget.textContent = message
    this.setLoaderMode("processing")
  }

  showLoaderSuccess(message = "Maps uploaded successfully") {
    clearTimeout(this.loaderResetTimer)
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hide", "failure")
      this.loaderTarget.classList.add("show", "success")
    }
    if (this.hasLoaderTextTarget) this.loaderTextTarget.textContent = message
    this.setLoaderMode("success")

    this.loaderResetTimer = setTimeout(() => {
      this.hideLoader()
    }, 2000)
  }

  showLoaderFailure(message = "Map upload failed") {
    clearTimeout(this.loaderResetTimer)
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hide", "success")
      this.loaderTarget.classList.add("show", "failure")
    }
    if (this.hasLoaderTextTarget) this.loaderTextTarget.textContent = message
    this.setLoaderMode("failure")
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("show")
      this.loaderTarget.classList.add("hide")
    }

    setTimeout(() => {
      this.resetLoader()
    }, 500)
  }

  resetLoader() {
    clearTimeout(this.loaderResetTimer)
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.remove("hide")
    if (this.hasLoaderTarget) this.loaderTarget.classList.remove("show", "hide", "success", "failure")
    if (this.hasLoaderTextTarget) this.loaderTextTarget.textContent = "Uploading Maps"
    this.setLoaderMode("processing")
  }

  setLoaderMode(mode) {
    if (!this.hasLoaderElementTarget) return
    this.loaderElementTarget.querySelector("[data-controller='loader']")?.setAttribute("data-loader-mode-value", mode)
  }

  refreshFeatureHeights() {
    [this.mapsContainerTarget, this.sensorsContainerTarget, this.groupsContainerTarget].forEach((feature) => {
      const checkbox = feature.nextElementSibling?.querySelector(".step-form-feature-flag-input")
      if (!checkbox) return

      feature.style.height = "auto"
      const height = feature.scrollHeight
      checkbox.dataset.height = `${height}px`
      feature.style.height = checkbox.checked ? `${height}px` : "0px"
    })
  }

  chunk(array, size) {
    const rows = []
    for (let i = 0; i < array.length; i += size) rows.push(array.slice(i, i + size))
    return rows
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  physicalScaleInMeters(project) {
    const scale = Number(project.lenPath)
    if (!Number.isFinite(scale) || scale <= 0 || !project.lenUnit) return ""

    return UnitConverter.convert(scale, project.lenUnit, "m")
  }

  refreshFeatureHeightsWhenImagesLoad() {
    const images = this.mapsContainerTarget.querySelectorAll("img")

    if (!images.length) {
      this.refreshFeatureHeights()
      return
    }

    let refreshed = false

    const refreshOnce = () => {
      if (refreshed) return
      refreshed = true
      requestAnimationFrame(() => this.refreshFeatureHeights())
    }

    images.forEach((image) => {
      if (image.complete && image.naturalHeight > 0) return

      image.addEventListener("load", refreshOnce, { once: true })
      image.addEventListener("error", refreshOnce, { once: true })
    })

    requestAnimationFrame(() => this.refreshFeatureHeights())
  }

  sensorNameChanged(event) {
    const block = event.target.closest(".event-sensor-block")
    if (!block) return

    const uiId = String(block.dataset.sensorUiId || "")
    const name = event.target.value || `Sensor ${uiId}`

    this.groupsContainerTarget
      .querySelectorAll("[data-group-sensor-ui-id]")
      .forEach((cell) => {
        if (String(cell.dataset.groupSensorUiId) === uiId) {
          cell.textContent = name
        }
      })

    if (this.project?.sensorNameById) {
      const numericId = Number(uiId)
      if (Number.isFinite(numericId)) {
        this.project.sensorNameById.set(numericId, name)
      }
    }
  }
}
